import React from 'react';
import { ClearFilterButton } from 'Shared/components/ClearFilterButtons';
import { MultiSelectInput } from 'Shared/components/FormInput/Variants/MultiSelect';
import {
  Button,
  Asset,
  CalendarIcon,
  DatePicker,
  Modal,
} from '@clearcover/atlas';
import { isBlank } from 'Shared/utils';
import { formatDateToLocalISO, parseLocalISODate } from 'Shared/utils/date';
import { FilterType, FilterOption } from './types';
import { getDefaultFilters } from './utils/filters';

import styles from './filters.module.css';

type FilterOptions = {
  assetTypes: FilterOption[];
  environments: FilterOption[];
  locales: FilterOption[];
  productTypes: FilterOption[];
  productSegments: FilterOption[];
  operatingCompanies: FilterOption[];
};

type StateConfigurationsFiltersProps = {
  filters: FilterType;
  onChangeFilters: (filters: FilterType) => void;
  filterOptions: FilterOptions;
};

export const StateConfigurationsFilters: React.FC<
  StateConfigurationsFiltersProps
> = ({ filters, onChangeFilters, filterOptions }) => {
  type StringFilterKeys = Exclude<keyof FilterType, 'activeToday'>;

  const handleFilterChange = (
    key: StringFilterKeys,
    selectedItems: FilterOption[]
  ) => {
    const newFilters = { ...filters };
    if (selectedItems.length > 0) {
      newFilters[key] = selectedItems.map((item) => item.key).join(',');
    } else {
      delete newFilters[key];
    }
    onChangeFilters(newFilters);
  };

  const clearAllFilters = () => {
    onChangeFilters(getDefaultFilters());
  };

  // Update filter options with selected state based on current filters
  const getOptionsWithSelection = (
    options: FilterOption[],
    filterKey: StringFilterKeys
  ): FilterOption[] => {
    const filterValue = filters[filterKey];
    const selectedValues =
      typeof filterValue === 'string' ? filterValue.split(',') : [];
    return options.map((option) => ({
      ...option,
      selected: selectedValues.includes(option.key),
    }));
  };

  return (
    <div className={styles.filtersContainer}>
      <MultiSelectInput
        label="State"
        items={getOptionsWithSelection(filterOptions.locales, 'locale')}
        onChangeSelection={(item) => {
          if (!item) return;
          const currentItems = getOptionsWithSelection(
            filterOptions.locales,
            'locale'
          ).filter((i) => i.selected);
          const itemExists = currentItems.some((i) => i.key === item?.key);
          const newItems = itemExists
            ? currentItems.filter((i) => i.key !== item?.key)
            : [...currentItems, item!];
          handleFilterChange('locale', newItems);
        }}
      />

      <MultiSelectInput
        label="Product Type"
        items={getOptionsWithSelection(
          filterOptions.productTypes,
          'productType'
        )}
        onChangeSelection={(item) => {
          if (!item) return;
          const currentItems = getOptionsWithSelection(
            filterOptions.productTypes,
            'productType'
          ).filter((i) => i.selected);
          const itemExists = currentItems.some((i) => i.key === item?.key);
          const newItems = itemExists
            ? currentItems.filter((i) => i.key !== item?.key)
            : [...currentItems, item!];
          handleFilterChange('productType', newItems);
        }}
      />

      <MultiSelectInput
        label="Product Segment"
        items={getOptionsWithSelection(
          filterOptions.productSegments,
          'productSegment'
        )}
        onChangeSelection={(item) => {
          if (!item) return;
          const currentItems = getOptionsWithSelection(
            filterOptions.productSegments,
            'productSegment'
          ).filter((i) => i.selected);
          const itemExists = currentItems.some((i) => i.key === item?.key);
          const newItems = itemExists
            ? currentItems.filter((i) => i.key !== item?.key)
            : [...currentItems, item!];
          handleFilterChange('productSegment', newItems);
        }}
      />

      <MultiSelectInput
        label="Operating Company"
        items={getOptionsWithSelection(
          filterOptions.operatingCompanies,
          'operatingCompany'
        )}
        onChangeSelection={(item) => {
          if (!item) return;
          const currentItems = getOptionsWithSelection(
            filterOptions.operatingCompanies,
            'operatingCompany'
          ).filter((i) => i.selected);
          const itemExists = currentItems.some((i) => i.key === item?.key);
          const newItems = itemExists
            ? currentItems.filter((i) => i.key !== item?.key)
            : [...currentItems, item!];
          handleFilterChange('operatingCompany', newItems);
        }}
      />

      <MultiSelectInput
        label="Asset Type"
        items={getOptionsWithSelection(filterOptions.assetTypes, 'assetType')}
        onChangeSelection={(item) => {
          if (!item) return;
          const currentItems = getOptionsWithSelection(
            filterOptions.assetTypes,
            'assetType'
          ).filter((i) => i.selected);
          const itemExists = currentItems.some((i) => i.key === item?.key);
          const newItems = itemExists
            ? currentItems.filter((i) => i.key !== item?.key)
            : [...currentItems, item!];
          handleFilterChange('assetType', newItems);
        }}
      />

      <MultiSelectInput
        label="Environment"
        items={getOptionsWithSelection(
          filterOptions.environments,
          'environment'
        )}
        onChangeSelection={(item) => {
          if (!item) return;
          const currentItems = getOptionsWithSelection(
            filterOptions.environments,
            'environment'
          ).filter((i) => i.selected);
          const itemExists = currentItems.some((i) => i.key === item?.key);
          const newItems = itemExists
            ? currentItems.filter((i) => i.key !== item?.key)
            : [...currentItems, item!];
          handleFilterChange('environment', newItems);
        }}
      />

      <Modal
        variant="normal"
        toggle={(open) => (
          <button
            className={styles.datePickerButton}
            onClick={open}
            disabled={!!filters.activeToday}
            type="button"
          >
            <span className={styles.label}>Effective When:</span>
            <div className={styles.dateValue}>
              {filters.effectiveDate
                ? parseLocalISODate(filters.effectiveDate).toLocaleDateString()
                : 'Select date'}
            </div>
            <Asset
              icon={CalendarIcon}
              description="Calendar"
              height={20}
              width={20}
              className={styles.calendarIcon}
            />
          </button>
        )}
      >
        {(close) => (
          <div className={styles.datePickerModal}>
            <div className={styles.datePickerHeader}>Select Effective Date</div>
            <DatePicker
              initializeDate={
                filters.effectiveDate
                  ? parseLocalISODate(filters.effectiveDate)
                  : new Date()
              }
              onSelect={(date) => {
                const newFilters = { ...filters };
                newFilters.effectiveDate = formatDateToLocalISO(date);
                delete newFilters.activeToday;
                onChangeFilters(newFilters);
                close();
              }}
            />
          </div>
        )}
      </Modal>

      <Button
        variant={filters.activeToday ? 'primary' : 'secondary'}
        onClick={() => {
          const newFilters = { ...filters };
          if (filters.activeToday) {
            delete newFilters.activeToday;
            delete newFilters.effectiveDate;
          } else {
            newFilters.activeToday = true;
            delete newFilters.effectiveDate;
          }
          onChangeFilters(newFilters);
        }}
        className={styles.activeTodayButton}
      >
        Active Today
      </Button>

      {!isBlank(filters) && (
        <ClearFilterButton
          onClick={clearAllFilters}
          disabled={false}
          className={styles.clearButton}
        >
          Clear Filters
        </ClearFilterButton>
      )}
    </div>
  );
};
